'use client';

import { type ReactNode, use, useEffect, useState } from 'react';

import { ArticleSectionRelated } from '@/app_components/ArticleSections/ArticleSectionRelated';
import { getSeenArticleIds } from '@/app_lib/services/recommendations/seenArticles';
import type { ArticlePreview } from '@/app_lib/types/ArticlePreview';
import type { EventData } from '@/app_lib/types/Gtm';

const recommendedTrackingData: EventData = {
  // biome-ignore lint/style/useNamingConvention: gtm data
  contentPlacementCD: 'recommended - bottom', //<module> - <position>
  eventAction: 'recommended module click',
  eventCategory: 'navigation',
};

interface RecommendedArticlesProps {
  currentArticleId: string;
  fallback: ReactNode;
  recommendationsPromise: Promise<ArticlePreview[] | null>;
}

export const RecommendedArticles = ({
  currentArticleId,
  fallback,
  recommendationsPromise,
}: RecommendedArticlesProps) => {
  const serverRecommendations = use(recommendationsPromise);
  const [clientRecommendations, setClientRecommendations] = useState<ArticlePreview[] | null>(null);

  useEffect(() => {
    const seenIds = getSeenArticleIds();
    if (seenIds.length === 0) {
      return;
    }

    const controller = new AbortController();
    const params = new URLSearchParams({
      context: 'article',
      currentArticleId,
      limit: '6',
    });
    for (const id of seenIds) {
      params.append('excludeIds', id);
    }

    const fetchRecommendations = async () => {
      try {
        const res = await fetch(`/api/recommendations?${params}`, { signal: controller.signal });
        if (!res.ok) {
          return;
        }
        const data: ArticlePreview[] = await res.json();
        if (data?.length > 0) {
          setClientRecommendations(data);
        }
      } catch {
        // AbortError or network failure; keep showing server-streamed data
      }
    };

    fetchRecommendations();

    return () => controller.abort();
  }, [currentArticleId]);

  const recommendations = clientRecommendations ?? serverRecommendations;

  if (!recommendations || recommendations.length === 0) {
    return <>{fallback}</>;
  }

  return (
    <ArticleSectionRelated
      articles={recommendations}
      heading='Recommended For You'
      moduleType='recommended'
      trackingData={recommendedTrackingData}
    />
  );
};
